import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const FaqSection = () => {
    const { t } = useTranslation('homepage');

    const faqData = [
        {
            question: t('faq-question-1'),
            answer: (
                <>
                    <p>{t('faq-answer-1-intro')}</p>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>{t('faq-answer-1-item-1')}</li>
                        <li>{t('faq-answer-1-item-2')}</li>
                        <li>{t('faq-answer-1-item-3')}</li>
                        <li>{t('faq-answer-1-item-4')}</li>
                    </ul>
                </>
            )
        },
        {
            question: t('faq-question-2'),
            answer: t('faq-answer-2')
        },
        {
            question: t('faq-question-3'),
            answer: (
                <>
                    <p>{t('faq-answer-3-intro')}</p>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>{t('faq-answer-3-item-1')}</li>
                        <li>{t('faq-answer-3-item-2')}</li>
                        <li>{t('faq-answer-3-item-3')}</li>
                        <li>{t('faq-answer-3-item-4')}</li>
                        <li>{t('faq-answer-3-item-5')}</li>
                        <li>{t('faq-answer-3-item-6')}</li>
                    </ul>
                </>
            )
        },
        {
            question: t('faq-question-4'),
            answer: (
                <>
                    <p>{t('faq-answer-4-intro')}</p>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>{t('faq-answer-4-item-1')}</li>
                        <li>{t('faq-answer-4-item-2')}</li>
                        <li>{t('faq-answer-4-item-3')}</li>
                        <li>{t('faq-answer-4-item-4')}</li>
                    </ul>
                    <p>{t('faq-answer-4-footer')}</p>
                </>
            )
        },
        {
            question: t('faq-question-5'),
            answer: (
                <>
                    <p>{t('faq-answer-5-intro')}</p>
                    <ul style={{ marginTop: 8, marginBottom: 16, paddingLeft: 20 }}>
                        <li>{t('faq-answer-5-item-1')}</li>
                        <li>{t('faq-answer-5-item-2')}</li>
                        <li>{t('faq-answer-5-item-3')}</li>
                        <li>{t('faq-answer-5-item-4')}</li>
                    </ul>
                    <div style={{ 
                        background: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        border: '1px solid #dee2e6',
                        marginTop: '16px'
                    }}>
                        <h6 style={{ marginBottom: '12px', color: '#495057', fontWeight: '600' }}>{t('faq-answer-5-sample-title')}</h6>
                        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            <p><strong>{t('faq-answer-5-sample-loan-amount-label')}</strong> INR 10,000</p>
                            <p><strong>{t('faq-answer-5-sample-loan-tenure-label')}</strong> 90 days</p>
                            <p><strong>{t('faq-answer-5-sample-interest-rate-label')}</strong> 18.25% Annualised</p>
                            <p><strong>{t('faq-answer-5-sample-account-fee-rate-label')}</strong> 54.75% Annualised</p>
                            <p><strong>{t('faq-answer-5-sample-apr-label')}</strong> 73% Annualised</p>
                            <p><strong>{t('faq-answer-5-sample-interest-amount-label')}</strong> INR 276</p>
                            <p><strong>{t('faq-answer-5-sample-account-fee-label')}</strong> INR 828</p>
                            <p><strong>{t('faq-answer-5-sample-gst-label')}</strong> INR 149</p>
                            <p><strong>{t('faq-answer-5-sample-emi-label')}</strong> INR 3,418</p>
                        </div>
                    </div>
                </>
            )
        }
    ];

    const [activeIndex, setActiveIndex] = useState(faqData.length - 1); // Last item expanded by default

    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? -1 : index);
    };

    // Staggered entrance animation
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.5,
                delayChildren: 0.5
            }
        }
    };

    const itemVariants = {
        hidden: { 
            opacity: 0, 
            y: 30,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        },
        hover: {
            y: -3,
            boxShadow: "0 10px 40px rgba(14, 41, 82, 0.4)",
            transition: {
                duration: 0.0,
                ease: "easeInOut"
            }
        }
    };

    // Answer expansion animation
    const answerVariants = {
        hidden: {
            opacity: 0,
            height: 0,
            transition: {
                duration: 0.1,
                ease: "easeInOut"
            }
        },
        visible: {
            opacity: 1,
            height: "auto",
            transition: {
                duration: 0.2,
                ease: "easeInOut"
            }
        }
    };

    return (
        <section className="homepage-faq-section mt-5 mb-4">
            <div className="homepage-faq-container">
                <motion.h2 
                    className="homepage-faq-title"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    viewport={{ once: true }}
                >
                    {t('faq-section-title')}
                </motion.h2>
                
                <motion.div 
                    className="homepage-faq-accordion"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    {faqData.map((faq, index) => (
                        <motion.div 
                            key={index} 
                            className={`homepage-faq-item ${activeIndex === index ? 'homepage-faq-item-active' : ''}`}
                            variants={itemVariants}
                            whileHover="hover"
                            onClick={() => toggleAccordion(index)}
                        >
                            <div className="homepage-faq-question">
                                <span>{faq.question}</span>
                                <div className="homepage-faq-icon">
                                    {activeIndex === index ? (
                                        <span className="homepage-faq-minus-icon">−</span>
                                    ) : (
                                        <span className="homepage-faq-plus-icon">+</span>
                                    )}
                                </div>
                            </div>
                            <AnimatePresence>
                                {activeIndex === index && (
                                    <motion.div 
                                        className="homepage-faq-answer"
                                        variants={answerVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="hidden"
                                    >
                                        {faq.answer}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default FaqSection;
